import { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import ModalButton from '../../common/ModalButton';
import CloseButton from '../../common/CloseButton';
import useMetronomeEngine from '../../../hooks/useMetronomeEngine';
import { getBeatsPerBar } from '../../../utils/songTiming';

import './RepeatTimerModal.scss';

const RepeatTimerModal = ({ isOpen, onClose, bpm, timeSignature, initialRepeatCount = 1, onInsert, lyrics }) => {
  const { t } = useTranslation();
  const [running, setRunning] = useState(false);
  const [countingIn, setCountingIn] = useState(false);
  const [countInBeat, setCountInBeat] = useState(null);
  const [elapsed, setElapsed] = useState(null);
  const [display, setDisplay] = useState(0);
  const [repeatCount, setRepeatCount] = useState(1);
  const [repeatCountInput, setRepeatCountInput] = useState('1');
  const [lyricsOpen, setLyricsOpen] = useState(false);
  // The beat of the bar the metronome last clicked, 1-based; 0 while it's silent
  const [beat, setBeat] = useState(0);
  const beatsPerBar = getBeatsPerBar(timeSignature);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);
  // Clicks heard since Start, while counting in; null otherwise
  const countInClicksRef = useRef(null);
  const metronomeDelayRef = useRef(null);
  const metronomeIdRef = useRef(`metronome-repeat-${Math.random()}`);
  const {
    start: startMetronome,
    stop: stopMetronome,
    prime: primeMetronome,
  } = useMetronomeEngine(metronomeIdRef.current, {
    onFlash: () => {
      setBeat((b) => (b % beatsPerBar) + 1);
      if (countInClicksRef.current !== null) countInClick();
    },
    onStopped: () => setBeat(0),
  });

  // The count-in is one bar of the song's own beats, stepped on the metronome's clicks. Timing
  // starts on the next downbeat, so the last number stays up for a full beat.
  const countInClick = () => {
    const click = ++countInClicksRef.current;

    if (click <= beatsPerBar) {
      setCountInBeat(click);
      return;
    }

    countInClicksRef.current = null;
    setCountingIn(false);
    setRunning(true);
    startTimeRef.current = Date.now();

    intervalRef.current = setInterval(() => {
      setDisplay((Date.now() - startTimeRef.current) / 1000);
    }, 100);
  };

  const clearCountIn = () => {
    if (metronomeDelayRef.current) clearTimeout(metronomeDelayRef.current);

    metronomeDelayRef.current = null;
    countInClicksRef.current = null;
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      primeMetronome();
      setRunning(false);
      setCountingIn(false);
      setCountInBeat(null);
      setElapsed(null);
      setDisplay(0);
      // The part's own count — set, or read off its slashes — so it rarely needs typing
      setRepeatCount(initialRepeatCount);
      setRepeatCountInput(String(initialRepeatCount));
      setLyricsOpen(false);
    } else {
      document.body.style.overflow = 'unset';

      clearCountIn();

      if (intervalRef.current) clearInterval(intervalRef.current);

      stopMetronome();
    }
    return () => {
      document.body.style.overflow = 'unset';
      clearCountIn();
      if (intervalRef.current) clearInterval(intervalRef.current);
      stopMetronome();
    };
  }, [isOpen, stopMetronome]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const handleStart = () => {
    clearCountIn();

    if (intervalRef.current) clearInterval(intervalRef.current);

    setElapsed(null);
    setDisplay(0);
    setBeat(0);
    setRunning(false);
    setCountingIn(true);
    setCountInBeat(null);

    countInClicksRef.current = 0;

    // Delay metronome start by one beat so "Get Ready" is always silent
    metronomeDelayRef.current = setTimeout(() => {
      startMetronome(bpm, timeSignature);
    }, 60000 / bpm);
  };

  const handleStop = () => {
    clearCountIn();
    setCountingIn(false);
    clearInterval(intervalRef.current);
    intervalRef.current = null;

    if (running) {
      const sec = (Date.now() - startTimeRef.current) / 1000;
      if (sec >= 1) {
        setElapsed(sec);
        setDisplay(sec);
      } else {
        setElapsed(null);
        setDisplay(0);
      }
    }

    setRunning(false);
    stopMetronome();
  };

  const handleInsert = () => {
    const secs = +elapsed.toFixed(1);

    onInsert(`{${repeatCount}x,${secs}}`);
    onClose();
  };

  const handleCountChange = (e) => {
    setRepeatCountInput(e.target.value);

    const parsed = parseInt(e.target.value);

    if (!isNaN(parsed) && parsed >= 1) setRepeatCount(parsed);
  };

  const handleCountBlur = () => {
    const clamped = Math.max(1, parseInt(repeatCountInput) || 1);

    setRepeatCount(clamped);
    setRepeatCountInput(String(clamped));
  };

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('RepeatTimerModal')) onClose();
  };

  const canInsert = elapsed !== null && !running && !countingIn;
  const isActive = countingIn || running;

  const content = (
    <div className="RepeatTimerModal" onClick={handleBackdropClick}>
      <div className="dialog">
        <div className="content">
          <div className="header">
            <h5 className="dialog-title">{t('dialogs.repeatTimer.title')}</h5>
            <CloseButton onClick={onClose} />
          </div>

          <div className="body">
            <ol className="steps">
              <li>{t('dialogs.repeatTimer.step1')}</li>
              <li>{t('dialogs.repeatTimer.step2')}</li>
              <li>{t('dialogs.repeatTimer.step3')}</li>
            </ol>

            <div className="bpm-display">
              <span className="tempo">
                <span className="value">{bpm}</span>
                <span className="label">BPM</span>
              </span>
              {/* One dot per beat of the bar; the lit one steps along with the metronome's clicks */}
              <span className="beats" aria-hidden="true">
                {Array.from({ length: beatsPerBar }, (_, i) => (
                  <span key={i} className={cx('beat', { on: beat === i + 1 })} />
                ))}
              </span>
            </div>

            <div className="stopwatch">
              {countingIn ? (
                <span className="count-in-display">
                  <span className={cx('count-in-bar', { ready: countInBeat === null })}>
                    {countInBeat ?? t('dialogs.repeatTimer.getReady')}
                  </span>
                </span>
              ) : (
                `${display.toFixed(1)}s`
              )}
            </div>

            <ModalButton
              block
              variant={isActive ? 'danger' : 'primary'}
              className="timer-btn"
              onClick={isActive ? handleStop : handleStart}
            >
              {isActive
                ? t('player.stop')
                : elapsed !== null
                  ? t('dialogs.repeatTimer.restart')
                  : t('dialogs.repeatTimer.start')}
            </ModalButton>

            <div className={cx('result-wrapper', { open: canInsert })}>
              <div className="inner">
                <div className="result">
                  <div className="count-row">
                    <span className="elapsed-label">{elapsed !== null ? elapsed.toFixed(1) : '0.0'}s</span>
                    <span className="operator">×</span>
                    <input
                      id="repeat-count"
                      type="number"
                      min="1"
                      max="99"
                      value={repeatCountInput}
                      onChange={handleCountChange}
                      onBlur={handleCountBlur}
                    />
                  </div>
                  <span className="formula-preview">{`{${repeatCount}x,${elapsed !== null ? +elapsed.toFixed(1) : 0}}`}</span>
                </div>
              </div>
            </div>

            {lyrics && (
              <div className="lyrics-section">
                <button type="button" className="lyrics-toggle" onClick={() => setLyricsOpen((o) => !o)}>
                  <span>{t('player.lyrics')}</span>
                  <span className={cx('arrow', { open: lyricsOpen })}>▾</span>
                </button>
                <div className={cx('lyrics-collapse', { open: lyricsOpen })}>
                  <div className="inner">
                    <pre className={cx('lyrics-block', { 'bar-chart': lyrics.includes('|') })}>{lyrics}</pre>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="footer">
            <ModalButton variant="secondary" onClick={onClose}>
              {t('common.cancel')}
            </ModalButton>
            <ModalButton disabled={!canInsert} onClick={handleInsert}>
              {t('dialogs.repeatTimer.insert')}
            </ModalButton>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default RepeatTimerModal;
